import { useEffect, useState } from 'react';

/**
 * Which user interaction(s) fire the trigger.
 * - "scroll": fires once the user has scrolled past `scrollThreshold`.
 * - "click": fires on the first click/tap.
 * - "all": fires on whichever of the above happens first.
 */
export type EventTriggerType = 'all' | 'scroll' | 'click';

export interface UseEventTriggerOptions {
  /** Which interaction(s) fire the trigger. Defaults to "scroll". */
  trigger?: EventTriggerType;
  /** Vertical scroll (px) required before the "scroll"/"all" trigger fires. Defaults to 40. */
  scrollThreshold?: number;
  /** Viewport width (px) at/under which the device is treated as mobile. Defaults to 768. */
  mobileBreakpoint?: number;
}

/**
 * Generic interaction-trigger hook, e.g. for deferring third-party scripts
 * (ads, widgets) until the user actually engages with the page. The gating
 * only applies on mobile — on desktop it returns `true` immediately, since
 * mobile is where deferring heavy scripts actually helps LCP.
 *
 * Must be used from a Client Component ("use client").
 *
 * @returns {boolean} Whether the trigger condition has been met.
 *
 * @example
 * const isTriggered = useEventTrigger({ trigger: 'scroll', scrollThreshold: 40 });
 * return isTriggered ? <Script src="..." strategy="lazyOnload" /> : null;
 */
export function useEventTrigger(options: UseEventTriggerOptions = {}): boolean {
  const {
    trigger = 'scroll',
    scrollThreshold = 40,
    mobileBreakpoint = 768,
  } = options;

  const [isTriggered, setIsTriggered] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Desktop: never gated, fire immediately.
    if (window.innerWidth > mobileBreakpoint) {
      setIsTriggered(true);
      return;
    }

    function removeListeners() {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    }

    function handleScroll() {
      if (window.scrollY >= scrollThreshold) {
        setIsTriggered(true);
        removeListeners();
      }
    }

    function handleInteraction() {
      setIsTriggered(true);
      removeListeners();
    }

    if (trigger !== 'click' && window.scrollY >= scrollThreshold) {
      setIsTriggered(true);
      return;
    }

    if (trigger === 'scroll' || trigger === 'all') {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }
    if (trigger === 'click' || trigger === 'all') {
      window.addEventListener('click', handleInteraction, { passive: true });
      window.addEventListener('touchstart', handleInteraction, { passive: true });
    }

    return removeListeners;
  }, [trigger, scrollThreshold, mobileBreakpoint]);

  return isTriggered;
}

export default useEventTrigger;
